/**
 * The cash-up screen's slice of the API, in the shape of `../api.ts`: bearer
 * token on every call, a typed error so a 409 on save reads as "a manager
 * already signed this night off" rather than "something went wrong".
 *
 * Everything crossing this boundary is integer cents. The screen converts to
 * dollars for the eye and back for the wire; nothing between here and the
 * table sees a decimal.
 */

import type { Cashup, CashupReconciliation, CashupWindow, PosDay } from "@baros/core";
import { request as shared } from "../api.js";

async function request<T>(
  token: string,
  method: "GET" | "PUT" | "POST",
  path: string,
  body?: unknown,
): Promise<T> {
  // The shared client: it refreshes an expired token once and ends
  // the session when it cannot (see `request` in src/api.ts).
  return shared<T>(token, method, path, body);
}

const v = (venueId: string) => encodeURIComponent(venueId);
const d = (serviceDate: string) => encodeURIComponent(serviceDate);

/** What the form sends. The server owns who counted it and when. */
export type CashupDraft = Omit<
  Cashup,
  "serviceDate" | "countedBy" | "status" | "updatedAt" | "finalisedAt" | "finalisedBy"
>;

/** A saved night and the reconciliation of it, as the API returns them. */
export interface CashupResponse {
  readonly cashup: Cashup;
  readonly reconciliation: CashupReconciliation;
}

/** The POS half of a cash-up, as `cashupFiguresFromPos` in core produces it. */
export type PosFigures = Pick<Cashup, "posCashCents" | "posCardCents" | "posOtherCents">;

export const cashupApi = {
  /** The fortnight, newest first. Omit the bounds for the default window. */
  list: (token: string, venueId: string, from?: string, to?: string) => {
    const query = new URLSearchParams();
    if (from) query.set("from", from);
    if (to) query.set("to", to);
    const suffix = query.toString() ? `?${query.toString()}` : "";
    return request<{ cashups: Cashup[]; window: CashupWindow }>(
      token,
      "GET",
      `/v1/venues/${v(venueId)}/cashups${suffix}`,
    );
  },

  /** One night. A 404 means nobody has cashed up yet — a normal state. */
  get: (token: string, venueId: string, serviceDate: string) =>
    request<CashupResponse>(
      token,
      "GET",
      `/v1/venues/${v(venueId)}/cashups/${d(serviceDate)}`,
    ),

  save: (token: string, venueId: string, serviceDate: string, body: CashupDraft) =>
    request<CashupResponse>(
      token,
      "PUT",
      `/v1/venues/${v(venueId)}/cashups/${d(serviceDate)}`,
      body,
    ),

  finalise: (token: string, venueId: string, serviceDate: string) =>
    request<CashupResponse>(
      token,
      "POST",
      `/v1/venues/${v(venueId)}/cashups/${d(serviceDate)}/finalise`,
    ),

  /**
   * What Square rang up that night, as the three figures the form asks for.
   * `cashup` is null when the till has nothing for the night; the call fails
   * outright where Square is not connected or the venue is not linked. Either
   * way the screen carries on without it — see docs/integrations.md.
   */
  posDay: (token: string, venueId: string, serviceDate: string) =>
    request<{ day: PosDay | null; cashup: PosFigures | null }>(
      token,
      "GET",
      `/v1/venues/${v(venueId)}/pos/days/${d(serviceDate)}`,
    ),
};
